import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '../stores/userStore'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        // Scroll to saved position on back/forward, otherwise scroll to top
        if (savedPosition) {
            return savedPosition
        }
        return { top: 0 }
    },
    routes: [
        {
            path: '/',
            name: 'home',
            component: () => import('../views/HomeView.vue'),
            meta: { title: 'Home' }
        },
        {
            path: '/search',
            name: 'search',
            component: () => import('../views/SearchView.vue'),
            meta: { title: 'Search' }
        },
        {
            path: '/releases',
            name: 'releases',
            component: () => import('../views/ReleasesView.vue'),
            meta: { title: 'Releases' }
        },
        {
            path: '/albums',
            name: 'albums',
            component: () => import('../views/AlbumsView.vue'),
            meta: { title: 'Albums' }
        },
        {
            path: '/charts',
            name: 'charts',
            component: () => import('../views/TopChartsView.vue'),
            meta: { title: 'Top Charts' }
        },
        {
            path: '/lists/:id',
            name: 'list-detail',
            component: () => import('../views/ListView.vue'),
            meta: { title: 'List Details' }
        },
        {
            path: '/festivals',
            name: 'festivals',
            component: () => import('../views/FestivalView.vue'),
            meta: { title: 'All Festivals' }
        },
        {
            path: '/festivals/upcoming',
            name: 'upcoming-festivals',
            component: () => import('../views/UpcomingFestivalsView.vue'),
            meta: { title: 'Upcoming Festivals' }
        },
        {
            path: '/festivals/incomplete',
            name: 'festivals-incomplete',
            component: () => import('../views/FestivalIncompleteView.vue'),
            meta: { title: 'Incomplete Festivals' }
        },
        {
            path: '/festivals/compare',
            name: 'FestivalCompare',
            component: () => import('../views/FestivalCompareView.vue'),
            meta: { title: 'Compare Festivals' }
        },
        {
            path: '/festival/:id',
            name: 'festival-detail',
            component: () => import('../views/FestivalDetailView.vue'),
            props: true,
            meta: { title: 'Festival Details' }
        },
        {
            path: '/album/:id',
            name: 'album-detail',
            component: () => import('../views/AlbumDetailView.vue'),
            props: true,
            meta: { title: 'Album Details' }
        },
        {
            path: '/artist/:id',
            name: 'artist-detail',
            component: () => import('../views/ArtistDetailView.vue'),
            props: true,
            meta: { title: 'Artist Details' }
        },
        {
            path: '/concert/:id',
            name: 'concert-detail',
            component: () => import('../views/ConcertDetailView.vue'),
            props: true,
            meta: { title: 'Concert Details' }
        },
        {
            path: '/festival-edition/:id',
            name: 'festival-edition-detail',
            component: () => import('../views/FestivalEditionDetailView.vue'),
            props: true,
            meta: { title: 'Festival Edition Details' }
        },
        {
            path: '/threads/:id',
            name: 'ThreadDetail',
            component: () => import('../views/ThreadDetailView.vue'),
            meta: { title: 'Discussion' }
        },
        {
            path: '/my-festivals',
            name: 'MyFestivals',
            component: () => import('../views/MyFestivalsView.vue'),
            meta: { title: 'My Festivals', requiresAuth: true }
        },
        {
            path: '/register',
            name: 'register',
            component: () => import('../views/RegisterView.vue'),
            meta: { title: 'Register' }
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('../views/LoginView.vue'),
            meta: { title: 'Login' }
        },
        {
            path: '/login/callback',
            name: 'login-callback',
            component: () => import('../views/LoginCallbackView.vue'),
            meta: { title: 'Logging in...' }
        },
        {
            path: '/forgot-password',
            name: 'forgot-password',
            component: () => import('../views/auth/RequestResetView.vue'),
            meta: { title: 'Forgot Password' }
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: () => import('../views/auth/ResetPasswordView.vue'),
            meta: { title: 'Reset Password' }
        },
        {
            path: '/verify-email',
            name: 'verify-email',
            component: () => import('../views/auth/VerifyEmailView.vue'),
            meta: { title: 'Verify Email' }
        },
        {
            path: '/profile',
            name: 'profile',
            component: () => import('../views/ProfileView.vue'),
            meta: { title: 'Profile', requiresAuth: true }
        },
        {
            path: '/profile/votes',
            name: 'user-votes',
            component: () => import('../views/UserVotesView.vue'),
            meta: { title: 'My Votes', requiresAuth: true }
        },
        {
            path: '/u/:id',
            name: 'public-profile',
            component: () => import('../views/PublicProfileView.vue'),
            meta: { title: 'User Profile' }
        },
        {
            path: '/admin',
            name: 'admin',
            component: () => import('../views/admin/AdminLayout.vue'),
            meta: { title: 'Admin', requiresAuth: true },
            redirect: '/admin/albums',
            children: [
                {
                    path: 'albums',
                    name: 'admin-albums',
                    component: () => import('../views/admin/AdminAlbumsView.vue'),
                    meta: { title: 'Admin - Albums', requiresAuth: true }
                },
                {
                    path: 'concerts',
                    name: 'admin-concerts',
                    component: () => import('../views/admin/AdminConcertsView.vue'),
                    meta: { title: 'Admin - Concerts', requiresAuth: true }
                },
                {
                    path: 'festivals',
                    name: 'admin-festivals',
                    component: () => import('../views/admin/AdminFestivalsView.vue'),
                    meta: { title: 'Admin - Festivals', requiresAuth: true }
                },
                {
                    path: 'users',
                    name: 'admin-users',
                    component: () => import('../views/admin/AdminUsersView.vue'),
                    meta: { title: 'Admin - Users', requiresAuth: true }
                }
            ]
        },
        {
            path: '/admin/reports',
            name: 'moderation',
            component: () => import('../views/ModerationView.vue'),
            meta: { title: 'Moderation', requiresAuth: true }
        },
        // Legal Pages
        {
            path: '/privacy',
            name: 'privacy',
            component: () => import('../views/PrivacyView.vue'),
            meta: { title: 'Privacy Policy' }
        },
        {
            path: '/terms',
            name: 'terms',
            component: () => import('../views/TermsView.vue'),
            meta: { title: 'Terms of Service' }
        },
        {
            path: '/cookies',
            name: 'cookies',
            component: () => import('../views/CookiePolicyView.vue'),
            meta: { title: 'Cookie Policy' }
        },
        {
            path: '/contact',
            name: 'contact',
            component: () => import('../views/ContactView.vue'),
            meta: { title: 'Contact Us' }
        },
        {
            path: '/contributors',
            name: 'contributors',
            component: () => import('../views/ContributorsView.vue'),
            meta: { title: 'Top Contributors' }
        },
        // 404 catch-all route - must be last
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: () => import('../views/NotFoundView.vue'),
            meta: { title: 'Page Not Found' }
        }
    ]
})

// Navigation guards
// Navigation guards
router.beforeEach(async (to, from, next) => {
    // Update document title
    const baseTitle = 'SendTheNoise'
    document.title = to.meta?.title
        ? `${to.meta.title} | ${baseTitle}`
        : baseTitle

    // Skip user initialization for the login callback route - it handles its own auth exchange
    if (to.path === '/login/callback') {
        next()
        return
    }

    // Ensure session is initialized (check cookies)
    const userStore = useUserStore()
    if (!userStore.initialized) {
        await userStore.initUser()
    }

    // Check auth for protected routes
    if (to.meta.requiresAuth) {
        if (!userStore.isLoggedIn) {
            next({ name: 'login', query: { redirect: to.fullPath } })
            return
        }

        // Enforce role-based access for admin routes
        if (to.path.startsWith('/admin')) {
            // Moderation page allows both admins and moderators
            if (to.path === '/admin/reports' || to.name === 'moderation') {
                if (!userStore.isModerator) {
                    next({ name: 'home' })
                    return
                }
            } else if (!userStore.isAdmin) {
                // All other admin routes require full admin role
                next({ name: 'home' })
                return
            }
        }
    }

    next()
})

// Global router error handling
router.onError((error) => {
    // Check if the error is related to chunk loading failure
    // This typically happens when a new version is deployed and old chunks are removed from the server
    const chunkFailedMessage = /Loading (chunk|CSS chunk) .+ failed/
    const isChunkLoadFailed = chunkFailedMessage.test(error.message) ||
        error.message.includes('Failed to fetch dynamically imported module')

    if (isChunkLoadFailed) {
        console.warn('Chunk loading failed. Force refreshing page...', error)
        window.location.reload()
    }
})

export default router
